import React, { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Listbox, Transition } from "@headlessui/react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { IoChevronDownOutline } from "react-icons/io5";
import AppContext from "../../context/AppContext";
import { fetchAndStoreUserPermissions } from "../../service/AuthService";
import { listOrganizations } from "../../service/CallCenterOrgAdmin";
import { getCallCenterApiErrorMessage } from "../../service/CallCenterApi";
import {
  getSelectedOrganizationId,
  isPlatformSuperAdmin,
  ORG_CONTEXT_CHANGED_EVENT,
  setSelectedOrganization,
} from "../../utils/organizationContext";
import { ORG_FEATURES_CHANGED_EVENT } from "../../utils/orgFeatures";
import { PERMISSIONS_CHANGED_EVENT } from "../../utils/permissions";
import { isDesktopApp } from "../../utils/isDesktop";
import { invalidateCachedRequestPrefix } from "../../service/callCenterRequestCache";

const formatOrgLabel = (org) => {
  if (!org) return "";
  const name = org.name || org.code || "";
  const code = org.code || "";
  return code && name !== code ? `${name} (${code})` : name || code;
};

const OrganizationContextSelector = () => {
  const { setSelectedOrgId, setSelectedOrgName, theme } = useContext(AppContext);
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [selectedId, setSelectedId] = useState(() => getSelectedOrganizationId() || "");
  const [accessTick, setAccessTick] = useState(0);

  const superAdmin = isPlatformSuperAdmin();

  useEffect(() => {
    const refresh = () => setAccessTick((value) => value + 1);
    window.addEventListener(PERMISSIONS_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(PERMISSIONS_CHANGED_EVENT, refresh);
  }, []);

  useEffect(() => {
    if (!superAdmin) return undefined;

    const syncFromStorage = () => {
      setSelectedId(getSelectedOrganizationId() || "");
    };
    window.addEventListener(ORG_CONTEXT_CHANGED_EVENT, syncFromStorage);
    return () => window.removeEventListener(ORG_CONTEXT_CHANGED_EVENT, syncFromStorage);
  }, [superAdmin, accessTick]);

  const applySelection = (org, broadcast = true) => {
    if (!org?.id) return;
    const id = String(org.id);
    setSelectedId(id);
    if (typeof setSelectedOrgId === "function") {
      setSelectedOrgId(org.id);
    }
    if (typeof setSelectedOrgName === "function") {
      setSelectedOrgName(org.name || org.code || "");
    }
    if (broadcast) {
      setSelectedOrganization(org.id, org.name || org.code || "");
      invalidateCachedRequestPrefix("");
    }
  };

  useEffect(() => {
    if (!superAdmin) return;

    let cancelled = false;
    const loadOrganizations = async () => {
      setLoading(true);
      try {
        const data = await listOrganizations({
          page: 0,
          size: 200,
          sortBy: "NAME",
          sortDir: "ASC",
          includeInactive: false,
        });
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setOrganizations(items);

        const storedId = getSelectedOrganizationId();
        const storedMatch = items.find((org) => String(org.id) === String(storedId));
        const defaultOrg =
          storedMatch ||
          items.find((org) => org.code === "EEMAD") ||
          items[0];

        if (defaultOrg && !storedMatch) {
          applySelection(defaultOrg);
        } else if (storedMatch) {
          applySelection(storedMatch, true);
        }
      } catch (error) {
        if (!cancelled) {
          const envelope = error?.envelope ?? error?.response?.data;
          toast.error(
            envelope != null
              ? getCallCenterApiErrorMessage(envelope, "Failed to load organizations")
              : "Failed to load organizations"
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, [superAdmin, accessTick]);

  const handleSelect = async (org) => {
    if (!org?.id || String(org.id) === String(selectedId) || switching) return;

    setSwitching(true);
    applySelection(org);
    try {
      const token = Cookies.get("accessToken");
      if (token) {
        await fetchAndStoreUserPermissions(token);
      }
      window.dispatchEvent(new CustomEvent(ORG_FEATURES_CHANGED_EVENT));
      invalidateCachedRequestPrefix("");
      if (!isDesktopApp()) {
        navigate("/call-center/dashboard", { replace: true });
      }
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      toast.error(
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, "Failed to switch organization context")
          : "Failed to switch organization context"
      );
    } finally {
      setSwitching(false);
    }
  };

  if (!superAdmin) {
    return null;
  }

  const selectedOrg = organizations.find((org) => String(org.id) === String(selectedId));
  const buttonLabel = selectedOrg
    ? formatOrgLabel(selectedOrg)
    : loading || switching
      ? "Loading..."
      : "No organizations";

  const buttonClass =
    theme === "dark"
      ? "border-gray-700 bg-gray-900 text-gray-100"
      : "border-slate-200/80 bg-white text-slate-700";

  return (
    <Listbox
      value={selectedOrg || null}
      onChange={handleSelect}
      disabled={loading || switching || organizations.length === 0}
    >
      {({ open }) => (
        <div className="relative w-[min(100%,240px)] min-w-[160px]">
          <Listbox.Button
            className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-start text-sm font-medium shadow-sm outline-none transition focus:border-emmad-400 focus:ring-2 focus:ring-emmad-500/15 ${buttonClass}`}
            aria-label="Select organization context"
          >
            <span className="min-w-0 flex-1 truncate" dir="auto">
              {buttonLabel}
            </span>
            <IoChevronDownOutline
              className={`h-4 w-4 shrink-0 text-slate-400 transition ${open ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </Listbox.Button>

          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute end-0 z-[120] mt-1 max-h-60 min-w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 text-sm shadow-xl">
              {organizations.map((org) => (
                <Listbox.Option
                  key={org.id}
                  value={org}
                  className={({ active }) =>
                    `cursor-pointer px-3 py-2 ${active ? "bg-slate-100 text-slate-900" : "text-slate-700"}`
                  }
                >
                  <span className="block truncate" dir="auto">
                    {formatOrgLabel(org)}
                  </span>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};

export default OrganizationContextSelector;
